import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibContainer } from '@chewy/kib-layout-react'

import SwapProductAlert from '~/components/common/dialog/swapProduct/shared/SwapProductAlert'
import SwapProductDetails from '~/components/common/dialog/swapProduct/shared/SwapProductDetails'
import { getSwapProductTrackingData } from '~/constants/swapProductAnalytics'
import type { Order, Prescription } from '~/types'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

import SwapRxSearch from './search/SwapRxSearch'

import styles from './SwapProductStep.module.scss'

interface SwapProductStepRef {
  getSelectedOrder: () => Order | null
}

interface SwapProductStepProps {
  onProceed?: (order: Order) => void
  prescription: Prescription
}

const SwapProductStep = forwardRef<SwapProductStepRef, SwapProductStepProps>(
  ({ onProceed, prescription }, ref) => {
    const changeType = 'product'
    const { t } = useTranslation('Dialogs')
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)

    useImperativeHandle(ref, () => ({
      getSelectedOrder: () => selectedOrder,
    }))

    const handleProceed = (order: Order) => {
      trackEvent(
        TrackingEvent.SWAP_PRODUCT_SEARCH_SELECT_PRODUCT,
        getSwapProductTrackingData(
          'SWAP_PRODUCT_SEARCH_SELECT_PRODUCT',
          prescription,
        ),
      )

      onProceed?.(order)
    }

    return (
      <div>
        <SwapProductAlert changeType={changeType} />
        <SwapProductDetails prescription={prescription} />
        <KibContainer className={styles.buttonWrapper}>
          <div className={styles.searchSection}>
            <h1>{t('SWAP_PRODUCT_DIALOG.RX_SEARCH')}</h1>
            <SwapRxSearch
              clientId={prescription.clientId}
              patientId={prescription.patientId}
              selectedOrder={selectedOrder}
              setSelectedOrder={setSelectedOrder}
            />
          </div>
          {onProceed && (
            <KibButtonNew
              disabled={selectedOrder === null}
              onClick={() => selectedOrder && handleProceed(selectedOrder)}
            >
              {t('SWAP_PRODUCT_DIALOG.PROCEED_BUTTON_LABEL', {
                changeType,
              })}
            </KibButtonNew>
          )}
        </KibContainer>
      </div>
    )
  },
)

export default SwapProductStep
